import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IUserTasksService } from "services/UserTasksService";
import { FileParameter, NewUserTaskModel, UserTaskModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { ICommentsService } from "services/CommentsService";
import { toast } from "react-toastify";

const initialState = {
  UserTask: new UserTaskModel({ title: "", description: "" }),
  EditingUserTask: false,
  LoadingUserTask: false,
  CreatedUserTaskID: 0,
};

export const userTaskSlice = createSlice({
  name: "userTask",
  initialState,
  reducers: {
    resetData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserTask.pending, (state) => {
        state.LoadingUserTask = true;
      })
      .addCase(getUserTask.rejected, (state) => {
        state.LoadingUserTask = false;
      })
      .addCase(getUserTask.fulfilled, (state, action) => {
        state.LoadingUserTask = false;
        state.UserTask = action.payload;
      })
      .addCase(createUserTask.pending, (state) => {
        state.EditingUserTask = true;
      })
      .addCase(updateUserTask.pending, (state) => {
        state.EditingUserTask = true;
      })
      .addCase(holdTask.pending, (state) => {
        state.EditingUserTask = true;
      })
      .addCase(cancelUserTask.pending, (state) => {
        state.EditingUserTask = true;
      })
      .addCase(createUserTask.rejected, (state) => {
        state.EditingUserTask = false;
      })
      .addCase(updateUserTask.rejected, (state) => {
        state.EditingUserTask = false;
      })
      .addCase(holdTask.rejected, (state) => {
        state.EditingUserTask = false;
      })
      .addCase(cancelUserTask.rejected, (state) => {
        state.EditingUserTask = false;
      })
      .addCase(createUserTask.fulfilled, (state, action) => {
        state.EditingUserTask = false;
        state.CreatedUserTaskID = action.payload;
        toast.success("User task created successfully!");
      })
      .addCase(updateUserTask.fulfilled, (state) => {
        state.EditingUserTask = false;
        toast.success("User task updated successfully!");
      })
      .addCase(holdTask.fulfilled, (state) => {
        state.EditingUserTask = false;
      })
      .addCase(cancelUserTask.fulfilled, (state) => {
        state.EditingUserTask = false;
        toast.info("User task has been cancelled!");
      });
  },
});

export const getUserTask = createAsyncThunk("UserTask/GetUserTask", async (userTaskID: number) => {
  const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
  return await userTasksService.getUserTask(userTaskID);
});

export const createUserTask = createAsyncThunk<
  number,
  { newUserTaskModel: NewUserTaskModel; commentThreadID: string | undefined }
>("UserTask/CreateUserTask", async ({ newUserTaskModel, commentThreadID }, thunkAPI) => {
  const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  const createdUserTaskID = await userTasksService.createUserTask(newUserTaskModel);

  if (commentThreadID) {
    await commentsService.createSystemComment({
      message: `A new task has been created: ${newUserTaskModel.title}`,
      private: true,
      commentThreadID: commentThreadID,
      attachments: new Array<FileParameter>(),
    });
  }

  thunkAPI.dispatch(getUserTask(createdUserTaskID));

  return createdUserTaskID;
});

export const updateUserTask = createAsyncThunk(
  "UserTask/UpdateUserTask",
  async (userTaskModel: UserTaskModel, thunkAPI) => {
    const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
    await userTasksService.updateUserTask(userTaskModel);
    thunkAPI.dispatch(getUserTask(userTaskModel.userTaskID!));
  }
);

export const holdTask = createAsyncThunk("UserTask/HoldTask", async (userTaskID: number, thunkAPI) => {
  const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
  await userTasksService.holdTask(userTaskID);
  thunkAPI.dispatch(getUserTask(userTaskID));
});

export const resumeTask = createAsyncThunk("UserTask/ResumeTask", async (userTaskID: number, thunkAPI) => {
  const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
  await userTasksService.resumeTask(userTaskID);
  thunkAPI.dispatch(getUserTask(userTaskID));
});

export const cancelUserTask = createAsyncThunk("UserTask/CancelUserTask", async (userTaskID: number, thunkAPI) => {
  const userTasksService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
  await userTasksService.cancelUserTask(userTaskID);
  thunkAPI.dispatch(getUserTask(userTaskID));
});

export const { resetData } = userTaskSlice.actions;

export const selectLoadingUserTask = (state: RootState) => {
  return state.userTask.LoadingUserTask;
};
export const selectEditingUserTask = (state: RootState) => {
  return state.userTask.EditingUserTask;
};
export const selectCreatedUserTaskID = (state: RootState) => {
  return state.userTask.CreatedUserTaskID;
};

export const selectUserTask = (state: RootState) => {
  return state.userTask.UserTask;
};

export default userTaskSlice.reducer;
